import { useNavigate, useParams } from "@tanstack/react-router";
import { Alert, Button } from "@/deps/ui-library";
import {
  useDiscardEmployeeMovement,
  useEmployeeMovement,
  usePostEmployeeMovement,
} from "@/api/queries";
import { useCan } from "@/app/session";
import {
  ApprovalActions,
  ApprovalCard,
  useApprovalHoldsDraft,
} from "@/components/DocumentApproval";
import { DocumentScreen } from "@/components/DocumentScreen";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

const ACCOUNT_LABEL = {
  employee_payable: "Employee payable",
  employee_advance: "Advances to employees",
};

/** One payment to or receipt from an employee. A draft follows the payment approval rule (ADR-0060). */
export function EmployeeMovementPage() {
  const { id } = useParams({ from: "/accounting/employees/movements/$id" });
  const navigate = useNavigate();
  const { data, isPending, error } = useEmployeeMovement(id);
  const post = usePostEmployeeMovement();
  const discard = useDiscardEmployeeMovement();
  const can = useCan();
  const draft = data?.number === null;
  const held = useApprovalHoldsDraft("employeePayment", id, draft);

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading voucher…</p>;
  if (error || !data)
    return <p className="text-bad-text p-6 text-sm">That voucher could not be loaded.</p>;

  const paying = data.kind === "payment";
  const failure = post.error ?? discard.error;

  return (
    <DocumentScreen
      title={paying ? "Payment to employee" : "Receipt from employee"}
      number={data.number}
      status={draft ? "draft" : "approved"}
      locked={!draft}
      subtitle={`${data.partyName} · ${formatDate(data.date)}`}
      actions={
        draft ? (
          <span className="flex items-center gap-2">
            {!held && can("accounting.employees.record") ? (
              <Button
                colorScheme="default"
                variant="ghost"
                size="sm"
                disabled={discard.isPending}
                onClick={() =>
                  discard.mutate(id, {
                    onSuccess: () => void navigate({ to: "/accounting/employees" }),
                  })
                }
              >
                Discard
              </Button>
            ) : null}
            <ApprovalActions
              kind="employeePayment"
              id={id}
              canCreate={can("purchases.payments.create")}
              canIssue={can("purchases.payments.post")}
              issuing={post.isPending}
              onIssue={() => post.mutate(id)}
              issueLabel="Post"
              approveLabel="Approve and post"
              workingLabel="Posting"
            />
          </span>
        ) : null
      }
      facts={[
        { label: paying ? "Paid to" : "Received from", value: data.partyName },
        { label: "Date", value: formatDate(data.date) },
        { label: "Account", value: ACCOUNT_LABEL[data.controlRole] },
        { label: paying ? "Paid from" : "Received into", value: data.cashAccountName },
        { label: "Amount", value: <span className="num">{formatAmount(data.amount)}</span> },
        ...(data.reference ? [{ label: "Reference", value: data.reference }] : []),
        ...(data.note ? [{ label: "Note", value: data.note }] : []),
      ]}
      aside={<ApprovalCard kind="employeePayment" id={id} />}
    >
      {failure ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(failure as Error).message}
        />
      ) : null}
    </DocumentScreen>
  );
}
